import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import DarkModeToggle from './DarkModeToggle.js';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Divider from '@mui/material/Divider';
import { useDarkMode } from '../DarkModeContext.js';
import './Header.css';

const Header = () => {
  const [query, setQuery] = useState('');
  const [cartItemCount, setCartItemCount] = useState(0);
  
  // UI States
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false); // Controls mobile search field

  const navigate = useNavigate();
  const { darkMode } = useDarkMode();

  // --- SEARCH FUNCTION ---
  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setMobileSearchOpen(false); // Close mobile search after submitting
      document.activeElement.blur(); // Hide keyboard
    }
  };

  // --- CATEGORY NAV ---

  // --- CART COUNT ---
  useEffect(() => {
    const updateCartItemCount = () => {
      const cart = JSON.parse(localStorage.getItem('cart')) || [];
      const count = cart.reduce((total, item) => total + item.quantity, 0);
      setCartItemCount(count);
    };
    updateCartItemCount();
    window.addEventListener('storage', updateCartItemCount);
    window.addEventListener('cartUpdated', updateCartItemCount);
    return () => {
      window.removeEventListener('storage', updateCartItemCount);
      window.removeEventListener('cartUpdated', updateCartItemCount);
    };
  }, []);

  // --- HEADER HEIGHT ADJUSTMENT ---
  const headerRef = useCallback(node => {
    if (node !== null) {
      const updateBodyPadding = () => {
        document.body.style.paddingTop = `${node.offsetHeight}px`;
      };
      updateBodyPadding();
      window.addEventListener('resize', updateBodyPadding);
      return () => window.removeEventListener('resize', updateBodyPadding);
    }
  }, []);

  return (
    <header className={`header ${darkMode ? 'dark-mode' : ''}`} ref={headerRef}>
      <div className="container-fluid">
        
        {/* ==============================
            MOBILE LAYOUT (< 768px) 
           ============================== */}
        <div className="d-flex align-items-center d-md-none justify-content-between">
            
            {/* STATE A: SEARCH IS CLOSED (Show Logo & Icons) */}
            {!mobileSearchOpen && (
                <>
                    <Link to="/" className="navbar-brand">
                        <img src={logo} alt="TGlobal" className="logo" />
                    </Link>

                    <div className="d-flex align-items-center">
                        {/* Search Trigger */}
                        <IconButton onClick={() => setMobileSearchOpen(true)} color="inherit">
                            <SearchIcon />
                        </IconButton>

                        {/* Cart */}
                        <IconButton onClick={() => navigate('/cart')} color="inherit">
                            <Badge badgeContent={cartItemCount} color="error" max={99}>
                                <ShoppingCartIcon />
                            </Badge>
                        </IconButton>

                        {/* Menu */}
                        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} color="inherit">
                            <DensityMediumIcon />
                        </IconButton>
                    </div>
                </>
            )}

            {/* STATE B: SEARCH IS OPEN (Show Input Field) */}
            {mobileSearchOpen && (
                <form onSubmit={handleSearch} className="d-flex w-100 align-items-center">
                    <input
                        autoFocus
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="form-control me-2"
                        placeholder="Search products..."
                        style={{ boxShadow: 'none' }} // Cleaner look
                    />
                    <IconButton onClick={() => setMobileSearchOpen(false)} color="inherit">
                        <CloseIcon />
                    </IconButton>
                </form>
            )}
        </div>

        {/* ==============================
            DESKTOP LAYOUT (>= 768px) 
           ============================== */}
        <div className="d-none d-md-flex row align-items-center w-100 m-0">
            {/* Logo */}
            <div className="col-2">
                <Link to="/" className="navbar-brand">
                    <img src={logo} alt="Trust Global" className="logo" />
                </Link>
            </div>

            {/* Search Bar (Always Visible on Desktop) */}
            <div className="col-8">
                <form onSubmit={handleSearch} className="d-flex w-100">
                    <div className="input-group">
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="form-control"
                            placeholder="Search parts, tools, equipment..."
                        />
                        <button className="btn btn-primary" type="submit">Search</button>
                    </div>
                </form>
            </div>

            {/* Icons */}
            <div className="col-2 d-flex justify-content-end align-items-center">
                <IconButton onClick={() => navigate('/cart')} color="inherit" className="me-2">
                    <Badge badgeContent={cartItemCount} color="error" max={99}>
                        <ShoppingCartIcon />
                    </Badge>
                </IconButton>

                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} color="inherit">
                    <DensityMediumIcon />
                </IconButton>
            </div>
        </div>

        {/* ==============================
            SHARED MENU (The "Tucked In" Categories)
           ============================== */}
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
                style: { width: 220, maxHeight: '80vh' },
            }}
        >
         
            
            
           
            
            {/* CATEGORIES LIST (Edit these as needed) */}
            <div className="ps-2">
                <MenuItem onClick={() => navigate('/category')} dense>Categories</MenuItem>
                <MenuItem onClick={() => navigate('/category')} dense>Brands</MenuItem>
                
                
                
            </div>


                 <Divider />
             <MenuItem>
                <ListItemIcon><DarkModeToggle /></ListItemIcon>
                <ListItemText primary="Theme" />
            </MenuItem>
        </Menu>

      </div>
    </header>
  );
};

export default Header;